import type { MouseEventHandler, ReactNode, Ref } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import {
  LIFECYCLE_LISTING_STATUSES,
  type LifecycleListingStatus,
  primaryListingStatus,
} from 'components/marketplace/helpers/listingLabels';
import { ListingPhotoActions } from 'components/marketplace/property/refresh/ListingPhotoActions';
import type { ListingCardData } from 'components/marketplace/property/types';
import { CardLink } from 'components/marketplace/PropertyCard';
import { formatCurrencyDollars } from 'helpers/formatters';

export const mapListingTitle = (listing: ListingCardData) =>
  listing.street || listing.fullAddress || 'View home';

const STATUS_TEXT = {
  pendingSale: 'Pending sale',
  comingSoon: 'Coming soon',
  recentlySold: 'Recently sold',
} satisfies Record<LifecycleListingStatus, string>;

const mapListingStatus = (listing: ListingCardData) => {
  const status = primaryListingStatus(listing.labels, LIFECYCLE_LISTING_STATUSES);
  return status ? STATUS_TEXT[status] : null;
};

export const MapListingPhotoFrame = ({
  listing,
  media,
  actions,
  photoRef,
  className,
}: {
  listing: ListingCardData;
  media: ReactNode;
  actions?: ReactNode;
  photoRef?: Ref<HTMLDivElement>;
  className?: string;
}) => {
  const status = mapListingStatus(listing);
  return (
    <Col
      ref={photoRef}
      className={cn(
        'relative aspect-[305/227] w-full overflow-hidden rounded-[20px] bg-od-bg-secondary',
        className,
      )}
    >
      {media}
      {status ? (
        <Text
          as="span"
          className="pointer-events-none absolute left-3 top-3 z-1 rounded-full border border-amber-300 bg-amber-100 px-3 py-1 text-body-xs text-od-content-primary"
        >
          {status}
        </Text>
      ) : null}
      {actions}
    </Col>
  );
};

export const MapListingDetails = ({
  listing,
  title,
}: {
  listing: ListingCardData;
  title: ReactNode;
}) => {
  const facts = [
    listing.beds == null ? null : `${listing.beds} bed`,
    listing.baths == null ? null : `${listing.baths} bath`,
    listing.livingSquareFootage == null
      ? null
      : `${listing.livingSquareFootage.toLocaleString('en-US')} sqft`,
  ].filter(Boolean);
  return (
    <Col className="gap-1">
      {title}
      <Row className="flex-wrap items-baseline gap-x-2 gap-y-0 text-body-sm leading-5">
        <Text as="span" className="text-body-sm leading-5">
          {listing.listPrice == null
            ? 'Price coming soon'
            : formatCurrencyDollars(listing.listPrice)}
        </Text>
        {listing.estimatedMonthlyPayment != null ? (
          <>
            <Text as="span" aria-hidden>
              ·
            </Text>
            <Text
              as="span"
              className="text-body-sm leading-5"
              title="Estimated monthly principal and interest with 20% down over 30 years. Taxes, insurance, and fees are excluded."
            >
              {formatCurrencyDollars(Math.round(listing.estimatedMonthlyPayment))} / mo
            </Text>
          </>
        ) : null}
        {listing.estimatedAnnualInterestRatePercent != null ? (
          <>
            <Text as="span" aria-hidden>
              ·
            </Text>
            <Text
              as="span"
              className="text-body-sm leading-5"
              title="Interest rate assumed for the payment estimate, not a personalized loan offer."
            >
              {listing.estimatedAnnualInterestRatePercent.toLocaleString('en-US', {
                maximumFractionDigits: 2,
              })}
              % rate
            </Text>
          </>
        ) : null}
      </Row>
      <Text as="p" className="text-body-sm leading-5 text-od-content-secondary">
        {facts.join(' · ')}
      </Text>
      {listing.labels?.includes('SELF_TOUR') ? (
        <Box className="self-start pt-[5px]">
          <Text
            as="span"
            className="rounded-md bg-od-bg-inactive-accent-brand/25 px-1.5 text-body-xs leading-[18px] text-od-content-accent-brand"
          >
            Self-tour available
          </Text>
        </Box>
      ) : null}
    </Col>
  );
};

export const MapListingCard = ({
  listing,
  href,
  media,
  isLiked,
  isFavoriteLoading,
  onToggleFavorite,
  onCopyLink,
  onOpen,
  photoRef,
}: {
  listing: ListingCardData;
  href: string;
  media: ReactNode;
  isLiked: boolean;
  isFavoriteLoading: boolean;
  onToggleFavorite: () => void;
  onCopyLink: () => void;
  onOpen?: MouseEventHandler<HTMLAnchorElement>;
  photoRef?: Ref<HTMLDivElement>;
}) => {
  const title = mapListingTitle(listing);
  return (
    <Col className="min-w-0 w-full gap-[17px]">
      <MapListingPhotoFrame
        listing={listing}
        photoRef={photoRef}
        media={media}
        actions={
          <ListingPhotoActions
            appearance="frosted"
            linkAction="copy"
            className="absolute right-3 top-3 z-2"
            linkAnalyticsName="map-refresh-card-copy-link"
            favoriteAnalyticsName="map-refresh-card-favorite"
            isLiked={isLiked}
            isFavoriteLoading={isFavoriteLoading}
            onLinkAction={onCopyLink}
            onToggleFavorite={onToggleFavorite}
          />
        }
      />
      <MapListingDetails
        listing={listing}
        title={
          <CardLink
            href={href}
            onClick={onOpen}
            external
            ariaLabel={`Property details: ${title}`}
            analyticsName="map-refresh-card-title"
            className="justify-start text-left"
          >
            <Text as="span" className="line-clamp-1 text-label-md">
              {title}
            </Text>
          </CardLink>
        }
      />
    </Col>
  );
};
